import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  Map as MapIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { formatDeviceLabel } from '../utils/deviceDisplayUtils';
import { DEFAULT_PARENT_MOVE_LIMIT, parentMoves } from '../utils/parentMoves';
import DiagnosticsParentMoveMap from './DiagnosticsParentMoveMap';
import DiagnosticsStabilityChart from './DiagnosticsStabilityChart';
import {
  DEFAULT_STABILITY_EVENT,
  DIAGNOSTICS_WINDOWS,
  STABILITY_EVENTS,
  VERDICT,
  isApStaChannelEvent,
  matchesDeviceQuery,
  matchesEventFilter,
  publicWifiTotals,
  sameReportGroups,
  stabilityScore,
  summarizeVerdicts,
} from '../utils/diagnosticsHealth';

const VERDICT_LABEL = {
  [VERDICT.HEALTHY]: 'Healthy',
  [VERDICT.WARNING]: 'Warning',
  [VERDICT.UNHEALTHY]: 'Unhealthy',
  [VERDICT.INSUFFICIENT]: 'Insufficient data',
  [VERDICT.NA]: 'N/A',
};

const VERDICT_COLOR = {
  [VERDICT.HEALTHY]: 'success',
  [VERDICT.WARNING]: 'warning',
  [VERDICT.UNHEALTHY]: 'error',
  [VERDICT.INSUFFICIENT]: 'default',
  [VERDICT.NA]: 'default',
};

const VERDICT_SORT = {
  [VERDICT.UNHEALTHY]: 4,
  [VERDICT.WARNING]: 3,
  [VERDICT.INSUFFICIENT]: 2,
  [VERDICT.HEALTHY]: 1,
  [VERDICT.NA]: 0,
};

const EVENT_STYLE = {
  reboot: { label: 'Reboot', color: '#d32f2f' },
  report_gap: { label: 'Report gap', color: '#6d4c41' },
  reconnect: { label: 'Reconnect', color: '#ed6c02' },
  parent_change: { label: 'Parent change', color: '#7b1fa2' },
  sw_version_change: { label: 'Firmware change', color: '#0288d1' },
  channel_change: { label: 'Channel change', color: '#00897b' },
  rssi_below_threshold: { label: 'RSSI below threshold', color: '#fbc02d' },
};

const STABILITY_PANEL_TITLE = {
  all: 'Backhaul events · all devices',
  backhaul: 'Backhaul events · devices flagged for backhaul',
};

const STABILITY_COLORS = Object.fromEntries(STABILITY_EVENTS.map(({ type }) => [type, EVENT_STYLE[type].color]));

const flagged = (counts) => counts.warning + counts.unhealthy;

const TOPICS = [
  {
    key: 'backhaul',
    title: 'Backhaul',
    detail: (summary) => `${flagged(summary.stability)} flagged for stability · ${flagged(summary.rssi)} for RSSI`,
  },
  {
    key: 'publicWifi',
    title: 'Public Wi-Fi sessions',
    detail: (summary) => `${summary.publicWifi.na} without enough sessions to grade`,
  },
];

const fmtNum = (v, digits = 0) => (Number.isFinite(v) ? v.toFixed(digits) : '—');
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

const fmtDuration = (sec) => {
  if (!Number.isFinite(sec)) return '—';
  if (sec < 60) return `${Math.round(sec)}s`;
  if (sec < 3600) return `${Math.round(sec / 60)}m`;
  const h = Math.floor(sec / 3600);
  const m = Math.round((sec % 3600) / 60);
  return m ? `${h}h ${m}m` : `${h}h`;
};

const VerdictChip = ({ verdict }) => (
  <Chip
    size="small"
    label={VERDICT_LABEL[verdict] || verdict}
    color={VERDICT_COLOR[verdict] || 'default'}
    variant={verdict === VERDICT.HEALTHY || verdict === VERDICT.NA ? 'outlined' : 'filled'}
  />
);

const SummaryCard = ({ title, counts, detail, active, onClick }) => (
  <Card variant="outlined" sx={{ borderColor: active ? 'primary.main' : undefined, borderWidth: active ? 2 : 1 }}>
    <CardActionArea onClick={onClick}>
      <CardContent>
        <Typography variant="subtitle2" gutterBottom>{title}</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip size="small" color="success" variant="outlined" label={`${counts.healthy} healthy`} />
          <Chip size="small" color="warning" label={`${counts.warning} warning`} />
          <Chip size="small" color="error" label={`${counts.unhealthy} unhealthy`} />
          {counts.insufficient > 0 && <Chip size="small" label={`${counts.insufficient} insufficient`} />}
        </Box>
        {detail && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>{detail}</Typography>
        )}
      </CardContent>
    </CardActionArea>
  </Card>
);

const eventDetail = (ev) => {
  switch (ev.type) {
    case 'reboot':
      return `uptime ${ev.from_value} s → ${ev.to_value} s`;
    case 'reconnect':
      return `parent ${ev.from_value}, previous session ${fmtDuration(ev.value)}`;
    case 'parent_change':
    case 'sw_version_change':
      return `${ev.from_value} → ${ev.to_value}`;
    case 'channel_change':
      return `${ev.radio_role || ev.radio_name}: ${ev.from_value} → ${ev.to_value}`;
    case 'report_gap':
      if (ev.cause === 'no_reports') return 'disconnected, no reports in this window';
      return `${ev.ended_at ? '' : 'still open, '}${fmtDuration(ev.duration_sec)}${ev.cause === 'nms_outage' ? ' (NMS outage, not scored)' : ''}`;
    case 'rssi_below_threshold':
      return `${ev.side === 'ap' ? 'parent side' : 'device side'}, min ${fmtNum(ev.value)} dBm, ${ev.samples} readings, ${fmtDuration(ev.duration_sec)}`;
    default:
      return '';
  }
};

const EventTimeline = ({ events, start, end, onShowMoves, device }) => {
  const startMs = new Date(start).getTime();
  const span = Math.max(new Date(end).getTime() - startMs, 1);
  const pct = (iso) => Math.min(100, Math.max(0, ((new Date(iso).getTime() - startMs) / span) * 100));
  const visible = useMemo(
    () => (events || []).filter((ev) => ev.type !== 'channel_change' || isApStaChannelEvent(ev, device)),
    [events, device],
  );
  const groups = useMemo(() => sameReportGroups(visible), [visible]);
  const moves = useMemo(() => parentMoves(visible), [visible]);
  const totalMoves = useMemo(() => parentMoves(visible, Infinity).length, [visible]);
  const moveStep = (ev) => moves.find((m) => m.event === ev)?.n || null;

  if (!visible.length) {
    return <Typography variant="body2" color="text.secondary">No events in this window.</Typography>;
  }
  return (
    <Box>
      {moves.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Button size="small" startIcon={<MapIcon />} onClick={() => onShowMoves(null)} sx={{ color: EVENT_STYLE.parent_change.color }}>
            {totalMoves > DEFAULT_PARENT_MOVE_LIMIT
              ? `Last ${DEFAULT_PARENT_MOVE_LIMIT} of ${totalMoves} parent changes on map`
              : `Parent changes on map (${totalMoves})`}
          </Button>
        </Box>
      )}
      <Box sx={{ position: 'relative', height: 28, bgcolor: 'action.hover', borderRadius: 1, mb: 1 }}>
        {visible.map((ev, i) => {
          const style = EVENT_STYLE[ev.type] || { color: '#9e9e9e', label: ev.type };
          const left = pct(ev.estimated_at || ev.event_at);
          const right = ev.ended_at ? pct(ev.ended_at) : (ev.type === 'report_gap' ? 100 : left);
          const width = Math.max(right - left, 0.4);
          return (
            <Tooltip key={i} title={`${style.label} · ${fmtTime(ev.event_at)} · ${eventDetail(ev)}`}>
              <Box
                sx={{
                  position: 'absolute',
                  left: `${left}%`,
                  width: `${width}%`,
                  minWidth: 3,
                  top: ev.type === 'rssi_below_threshold' ? (ev.side === 'ap' ? 16 : 4) : 4,
                  height: ev.type === 'rssi_below_threshold' ? 8 : 20,
                  bgcolor: style.color,
                  opacity: ev.cause === 'nms_outage' ? 0.35 : 0.9,
                  borderRadius: 0.5,
                }}
              />
            </Tooltip>
          );
        })}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="caption" color="text.secondary">{fmtTime(start)}</Typography>
        <Typography variant="caption" color="text.secondary">{fmtTime(end)}</Typography>
      </Box>
      <Table
        size="small"
        sx={{
          '& .MuiTableCell-root': { py: 0.75, pl: 0, pr: 4 },
          '& .MuiTableCell-root:nth-of-type(1)': { width: 240, whiteSpace: 'nowrap', pl: 1.5 },
          '& .MuiTableCell-root:nth-of-type(2)': { width: 260, whiteSpace: 'nowrap' },
          '& .MuiTableCell-root:last-of-type': { pr: 0 },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell>Event</TableCell>
            <TableCell>Seen at</TableCell>
            <TableCell>Details</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {visible.map((ev, i) => {
            const step = ev.type === 'parent_change' ? moveStep(ev) : null;
            const { size, index } = groups[i];
            const grouped = size > 1;
            return (
              <TableRow
                key={i}
                hover={Boolean(step)}
                onClick={step ? () => onShowMoves(step) : undefined}
                sx={{
                  ...(step ? { cursor: 'pointer' } : {}),
                  ...(grouped && index < size - 1 ? { '& > td': { borderBottom: 'none' } } : {}),
                }}
              >
                <TableCell sx={grouped ? { boxShadow: (theme) => `inset 3px 0 0 ${theme.palette.primary.light}` } : undefined}>
                  <Box component="span" sx={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', bgcolor: (EVENT_STYLE[ev.type] || {}).color, mr: 1 }} />
                  {(EVENT_STYLE[ev.type] || {}).label || ev.type}
                </TableCell>
                <TableCell>
                  {index === 0 ? fmtTime(ev.event_at) : (
                    <Typography component="span" variant="body2" color="text.secondary">↳ same report</Typography>
                  )}
                  {grouped && index === 0 && (
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      {`· ${size} events`}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {eventDetail(ev)}
                  {step && (
                    <Tooltip title="Show on map">
                      <MapIcon fontSize="inherit" sx={{ ml: 1, verticalAlign: 'middle', color: EVENT_STYLE.parent_change.color }} />
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
};

const deviceLabel = (device, serial) => (device ? formatDeviceLabel(device) : serial);

const buildColumns = (rssiThresholdDbm) => [
  {
    key: 'device',
    label: 'Device',
    tooltip: 'Serial and name from the device list.',
    sortValue: ({ row, device }) => deviceLabel(device, row.device_mac_address),
  },
  {
    key: 'parent',
    label: 'Parent',
    tooltip: 'Current parent, kept while overlapping parents list this device.',
    sortValue: ({ row }) => row.uplink?.parent_latest || '',
  },
  { key: 'backhaul', label: 'Backhaul', tooltip: 'Worse of the stability and RSSI verdicts. Too few reports mark it as insufficient data.', sortValue: ({ verdicts }) => VERDICT_SORT[verdicts.backhaul] },
  { key: 'stability', label: 'Stability', tooltip: 'Reconnects, parent changes, and reboots per 24 hours. Any report gap is unhealthy.', sortValue: ({ verdicts }) => VERDICT_SORT[verdicts.stability] },
  { key: 'rssi', label: 'RSSI', tooltip: 'Worse of the device-side and parent-side p10 RSSI. An asymmetry above the limit is at least a warning.', sortValue: ({ verdicts }) => VERDICT_SORT[verdicts.rssi] },
  { key: 'publicWifi', label: 'Public Wi-Fi', tooltip: 'Share of short public Wi-Fi sessions, graded on interfaces with enough sessions.', sortValue: ({ verdicts }) => VERDICT_SORT[verdicts.publicWifi] },
  { key: 'reboots', label: 'Reboots', tooltip: 'Uptime dropped by more than 60 seconds between reports.', sortValue: ({ row }) => row.event_counts?.reboots || 0, numeric: true },
  { key: 'reconnects', label: 'Reconnects', tooltip: 'The parent\'s connected time for this device restarted, with the same parent.', sortValue: ({ row }) => row.event_counts?.reconnects || 0, numeric: true },
  { key: 'parentChanges', label: 'Parent changes', tooltip: 'The device moved to a different parent. Overlapping listings are not counted.', sortValue: ({ row }) => row.event_counts?.parent_changes || 0, numeric: true },
  { key: 'gaps', label: 'Report gaps', tooltip: 'More than 10 minutes with no report. NMS outages are shown on the timeline and not counted.', sortValue: ({ row }) => row.event_counts?.report_gaps || 0, numeric: true },
  { key: 'staRssi', label: 'Device RSSI p10 / avg', tooltip: 'RSSI measured by this device on its backhaul interface, in dBm.', sortValue: ({ row }) => row.uplink?.sta_rssi?.p10 ?? 0, numeric: true },
  { key: 'apRssi', label: 'Parent RSSI p10 / avg', tooltip: 'RSSI of this device as measured by its parent, in dBm.', sortValue: ({ row }) => row.uplink?.ap_rssi?.p10 ?? 0, numeric: true },
  {
    key: 'belowSec',
    label: `Below ${rssiThresholdDbm} dBm`,
    tooltip: 'Time spent below the RSSI threshold on the worse side.',
    sortValue: ({ row }) => Math.max(row.event_counts?.rssi_below_threshold_sec_sta || 0, row.event_counts?.rssi_below_threshold_sec_ap || 0),
    numeric: true,
  },
  { key: 'shortPct', label: 'Short sessions', tooltip: 'Public Wi-Fi sessions under 5 minutes, as a share of completed sessions.', sortValue: ({ row }) => publicWifiTotals(row).shortPct ?? -1, numeric: true },
  { key: 'sessions', label: 'Sessions', tooltip: 'Completed public Wi-Fi sessions in the window.', sortValue: ({ row }) => publicWifiTotals(row).sessions, numeric: true },
];

const ThresholdField = ({ label, value, onCommit, helperText }) => {
  const [text, setText] = useState(String(value));
  useEffect(() => { setText(String(value)); }, [value]);
  const commit = () => {
    const n = parseFloat(text);
    if (Number.isFinite(n)) onCommit(n);
    else setText(String(value));
  };
  return (
    <TextField
      size="small"
      type="number"
      label={label}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') commit(); }}
      helperText={helperText}
      fullWidth
    />
  );
};

const DiagnosticsView = ({
  diagnostics,
  evaluated,
  timeWindow,
  onTimeWindowChange,
  rssiThresholdDbm,
  onRssiThresholdChange,
  thresholds,
  onThresholdsChange,
  isLoading,
  error,
  onRefresh,
  devicesBySerial = {},
}) => {
  const navigate = useNavigate();
  const [moveMap, setMoveMap] = useState(null);
  const [search, setSearch] = useState('');
  const [topicFilter, setTopicFilter] = useState(null);
  const [eventFilter, setEventFilter] = useState([DEFAULT_STABILITY_EVENT]);
  const [sort, setSort] = useState({ key: 'belowSec', desc: true });
  const [expanded, setExpanded] = useState(null);
  const [showThresholds, setShowThresholds] = useState(false);

  const columns = useMemo(() => buildColumns(rssiThresholdDbm), [rssiThresholdDbm]);
  const summary = useMemo(() => summarizeVerdicts(evaluated), [evaluated]);

  const topicRows = useMemo(() => (topicFilter
    ? evaluated.filter(({ verdicts }) => verdicts[topicFilter] === VERDICT.WARNING || verdicts[topicFilter] === VERDICT.UNHEALTHY)
    : evaluated), [evaluated, topicFilter]);

  const showStability = !topicFilter || topicFilter === 'backhaul';

  const selectTopic = (key) => {
    const next = topicFilter === key ? null : key;
    setTopicFilter(next);
    if (next && next !== 'backhaul') setEventFilter([]);
    else if (topicFilter && topicFilter !== 'backhaul') setEventFilter([DEFAULT_STABILITY_EVENT]);
  };

  const toggleEvent = (type) => {
    setEventFilter((cur) => (cur.includes(type) ? cur.filter((t) => t !== type) : [...cur, type]));
  };

  const rows = useMemo(() => {
    let list = topicRows;
    if (eventFilter.length) {
      list = list.filter(({ row, device }) => matchesEventFilter(row, device, eventFilter));
    }
    if (search.trim()) {
      list = list.filter(({ row, device }) => matchesDeviceQuery(row, device, search));
    }
    const col = columns.find((c) => c.key === sort.key) || columns[2];
    const sorted = [...list].sort((a, b) => {
      const va = col.sortValue(a);
      const vb = col.sortValue(b);
      let cmp = typeof va === 'string' ? String(va).localeCompare(String(vb)) : (va ?? 0) - (vb ?? 0);
      if (cmp === 0) cmp = stabilityScore(a.row) - stabilityScore(b.row);
      return sort.desc ? -cmp : cmp;
    });
    return sorted;
  }, [topicRows, eventFilter, search, sort, columns]);

  const stabilityRows = useMemo(
    () => topicRows.map(({ row, device }) => (device ? { ...row, device } : row)),
    [topicRows],
  );

  const windowStart = diagnostics?.window_start;
  const windowEnd = diagnostics?.window_end;
  const coverageStart = diagnostics?.coverage_start;
  const coverageShort = coverageStart && windowStart && new Date(coverageStart) > new Date(windowStart);

  const setThreshold = (key) => (v) => onThresholdsChange({ ...thresholds, [key]: v });

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" gutterBottom>Network Diagnostics</Typography>
          <Typography variant="body2" color="text.secondary">
            Backhaul stability, backhaul RSSI in both directions, and public Wi-Fi session length per device.
          </Typography>
          {coverageShort && (
            <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
              Data since {fmtTime(coverageStart)}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search name or MAC"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ width: 220 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
            }}
          />
          <ToggleButtonGroup
            size="small"
            exclusive
            value={timeWindow}
            onChange={(_, v) => { if (v) onTimeWindowChange(v); }}
          >
            {DIAGNOSTICS_WINDOWS.map((w) => (
              <ToggleButton key={w.value} value={w.value}>{w.label}</ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Box sx={{ width: 150 }}>
            <ThresholdField label="RSSI threshold (dBm)" value={rssiThresholdDbm} onCommit={(v) => onRssiThresholdChange(Math.min(-30, Math.max(-100, v)))} />
          </Box>
          <Button size="small" onClick={() => setShowThresholds((s) => !s)}>
            {showThresholds ? 'Hide thresholds' : 'Thresholds'}
          </Button>
          <Tooltip title="Refresh (results are cached for 5 minutes)">
            <span>
              <IconButton onClick={onRefresh} disabled={isLoading}>
                {isLoading ? <CircularProgress size={20} /> : <RefreshIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {error && <Alert severity="warning" sx={{ mb: 2 }}>Refresh failed: {error}</Alert>}

      <Collapse in={showThresholds}>
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={6} md={2}><ThresholdField label="Stability warning / 24h" value={thresholds.stabilityWarnPer24h} onCommit={setThreshold('stabilityWarnPer24h')} /></Grid>
            <Grid item xs={6} md={2}><ThresholdField label="Stability unhealthy / 24h" value={thresholds.stabilityUnhealthyPer24h} onCommit={setThreshold('stabilityUnhealthyPer24h')} /></Grid>
            <Grid item xs={6} md={2}><ThresholdField label="RSSI healthy (dBm)" value={thresholds.rssiHealthyDbm} onCommit={setThreshold('rssiHealthyDbm')} /></Grid>
            <Grid item xs={6} md={2}><ThresholdField label="RSSI unhealthy (dBm)" value={thresholds.rssiUnhealthyDbm} onCommit={setThreshold('rssiUnhealthyDbm')} /></Grid>
            <Grid item xs={6} md={2}><ThresholdField label="Short sessions warning %" value={thresholds.publicShortWarnPct} onCommit={setThreshold('publicShortWarnPct')} /></Grid>
            <Grid item xs={6} md={2}><ThresholdField label="Short sessions unhealthy %" value={thresholds.publicShortUnhealthyPct} onCommit={setThreshold('publicShortUnhealthyPct')} /></Grid>
          </Grid>
        </Paper>
      </Collapse>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {TOPICS.map((t) => (
          <Grid item xs={12} md={6} key={t.key}>
            <SummaryCard
              title={t.title}
              counts={summary[t.key]}
              detail={t.detail(summary)}
              active={topicFilter === t.key}
              onClick={() => selectTopic(t.key)}
            />
          </Grid>
        ))}
      </Grid>

      {evaluated.length > 0 && showStability && (
        <DiagnosticsStabilityChart
          title={STABILITY_PANEL_TITLE[topicFilter || 'all']}
          rows={stabilityRows}
          start={windowStart}
          end={windowEnd}
          colors={STABILITY_COLORS}
          labels={{ rssi_below_threshold: `Signal below ${rssiThresholdDbm} dBm` }}
          active={eventFilter}
          onSelect={toggleEvent}
        />
      )}

      {rows.length === 0 ? (
        <Alert severity="info">
          {search.trim() ? 'No devices match the search.' : topicFilter || eventFilter.length ? 'No devices match the selected filters.' : 'No diagnostics data for this window yet.'}
        </Alert>
      ) : (
        <TableContainer>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell />
                {columns.map((col) => (
                  <TableCell key={col.key} align={col.numeric ? 'right' : 'left'} sortDirection={sort.key === col.key ? (sort.desc ? 'desc' : 'asc') : false}>
                    <Tooltip title={col.tooltip}>
                      <TableSortLabel
                        active={sort.key === col.key}
                        direction={sort.key === col.key && !sort.desc ? 'asc' : 'desc'}
                        onClick={() => setSort((s) => ({ key: col.key, desc: s.key === col.key ? !s.desc : true }))}
                      >
                        {col.label}
                      </TableSortLabel>
                    </Tooltip>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((item) => {
                const { row, device, verdicts } = item;
                const serial = String(row.device_mac_address || '').toLowerCase();
                const isOpen = expanded === serial;
                const counts = row.event_counts || {};
                const pub = publicWifiTotals(row);
                const below = Math.max(counts.rssi_below_threshold_sec_sta || 0, counts.rssi_below_threshold_sec_ap || 0);
                return (
                  <React.Fragment key={serial}>
                    <TableRow hover>
                      <TableCell padding="checkbox">
                        <IconButton size="small" onClick={() => setExpanded(isOpen ? null : serial)}>
                          {isOpen ? <CollapseIcon /> : <ExpandIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                          onClick={() => navigate(`/device/${serial}`)}
                        >
                          {deviceLabel(device, serial)}
                        </Typography>
                        {row.connected === false && (
                          <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                            {row.last_seen ? `Offline since ${fmtTime(row.last_seen)}` : 'Offline'}
                          </Typography>
                        )}
                        {row.no_reports && row.connected !== false && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Connected, no diagnostics data
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{row.uplink?.parent_latest || '—'}</TableCell>
                      <TableCell><VerdictChip verdict={verdicts.backhaul} /></TableCell>
                      <TableCell><VerdictChip verdict={verdicts.stability} /></TableCell>
                      <TableCell><VerdictChip verdict={verdicts.rssi} /></TableCell>
                      <TableCell><VerdictChip verdict={verdicts.publicWifi} /></TableCell>
                      <TableCell align="right">{counts.reboots || 0}</TableCell>
                      <TableCell align="right">{counts.reconnects || 0}</TableCell>
                      <TableCell align="right">{counts.parent_changes || 0}</TableCell>
                      <TableCell align="right">{counts.report_gaps || 0}</TableCell>
                      <TableCell align="right">{`${fmtNum(row.uplink?.sta_rssi?.p10)} / ${fmtNum(row.uplink?.sta_rssi?.avg, 1)}`}</TableCell>
                      <TableCell align="right">{`${fmtNum(row.uplink?.ap_rssi?.p10)} / ${fmtNum(row.uplink?.ap_rssi?.avg, 1)}`}</TableCell>
                      <TableCell align="right">{below ? fmtDuration(below) : '—'}</TableCell>
                      <TableCell align="right">{pub.shortPct === null ? '—' : `${fmtNum(pub.shortPct, 1)}%`}</TableCell>
                      <TableCell align="right">{pub.sessions || '—'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={columns.length + 1} sx={{ py: 0, borderBottom: isOpen ? undefined : 'none' }}>
                        <Collapse in={isOpen} unmountOnExit>
                          <Box sx={{ py: 2 }}>
                            {verdicts.lowConfidence && (
                              <Alert severity="info" sx={{ mb: 1 }}>
                                {`Only ${row.reporting?.samples || 0} reports in this window, fewer than half of what the report interval implies.`}
                              </Alert>
                            )}
                            {row.events_truncated && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                Showing the newest 500 events. Counts include all events.
                              </Typography>
                            )}
                            <EventTimeline
                              events={row.events || []}
                              start={windowStart}
                              end={windowEnd}
                              device={device}
                              onShowMoves={(step) => setMoveMap({ serial, events: row.events || [], step })}
                            />
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {moveMap && (
        <DiagnosticsParentMoveMap
          open
          onClose={() => setMoveMap(null)}
          serial={moveMap.serial}
          events={moveMap.events}
          initialStep={moveMap.step}
          devicesBySerial={devicesBySerial}
        />
      )}
    </Paper>
  );
};

export default DiagnosticsView;
